/**
 * Module dependencies.
 */

import { Input, Radio } from 'src/components/core';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { StepPageConfig } from 'src/types/advisor';
import { ifProp, theme } from 'styled-tools';
import { modelRangeSpecsMeasures } from 'src/core/content-config/model-ranges';
import {
  stepsKeys,
  waterCapacityOtherOptions
} from 'src/core/advisor-config/steps-options';

import { units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import BaseStep from './base-step';
import ContactFormStep from './contact-form';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  margin-left: -${units(1)};
  position: relative;
`;

/**
 * `OptionsWrapper` styled component.
 */

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
  margin-bottom: ${units(6)};
`;

/**
 * `OtherWrapper` styled component.
 */

const OtherWrapper = styled.div`
  display: grid;
  grid-column-gap: ${units(5)};
  grid-template-columns: repeat(2, max-content);
`;

/**
 * `StyledRadio` styled component.
 */

const StyledRadio = styled(Radio)`
  font-weight: 700;
`;

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(Input)<{ isVisible: boolean }>`
  margin-bottom: 0;
  margin-top: -${units(1)};
  opacity: ${ifProp('isVisible', 1, 0)};
  pointer-events: ${ifProp('isVisible', 'auto', 'none')};
  transition: opacity ${theme('animations.defaultTransition')};
  width: ${units(18)};
`;

/**
 * `StepOtherOption` component.
 */

const StepOtherOption = (props: Props): ReactElement => {
  const {
    className,
    filterKey,
    hasTranslatedOptions,
    options,
    pageIndex,
    pageKey
  } = props;

  const {
    filteredModelRanges,
    getCheckedValue,
    isSuccessModelRanges,
    onGoToNext,
    onGoToPrevious,
    onSetFilter,
    refetch
  } = useAdvisor();

  const { translate } = useTranslate();
  const checkedValue = getCheckedValue(pageKey);
  const hasOtherValue = useMemo(() => {
    if (isEmpty(checkedValue) || typeof options[0] !== 'number') {
      return false;
    }

    return !includes(
      options as Exclude<Props['options'], number[]>,
      `${checkedValue}`
    );
  }, [checkedValue, options]);

  const [value, setValue] = useState<string | null>(checkedValue);
  const [showInput, setShowInput] = useState<boolean>(hasOtherValue);
  const [isOtherOptionChecked, setOtherOptionChecked] =
    useState<boolean>(hasOtherValue);

  const canContinue = useMemo(() => {
    if (isOtherOptionChecked) {
      return !isEmpty(value);
    }

    return !isEmpty(checkedValue) || !isEmpty(value);
  }, [checkedValue, isOtherOptionChecked, value]);

  const handleChange = useCallback(
    ({ target }) => {
      setOtherOptionChecked(false);
      setValue(target.value);

      if (filterKey) {
        onSetFilter(filterKey, JSON.stringify(target.value));
        refetch();
      }

      onGoToNext(target.value, pageKey, pageIndex);
    },
    [filterKey, onGoToNext, onSetFilter, pageIndex, pageKey, refetch]
  );

  const handleContinue = useCallback(() => {
    if (value) {
      if (filterKey) {
        onSetFilter(filterKey, JSON.stringify(value));
        refetch();
      }

      onGoToNext(value, pageKey, pageIndex);
    }
  }, [filterKey, onGoToNext, onSetFilter, pageIndex, pageKey, refetch, value]);

  const handleCheckOtherOption = useCallback(() => {
    setShowInput(true);
    setOtherOptionChecked(true);
  }, []);

  if (isSuccessModelRanges && isEmpty(filteredModelRanges)) {
    return <ContactFormStep />;
  }

  return (
    <Wrapper className={className}>
      <BaseStep
        canContinue={canContinue}
        onContinue={handleContinue}
        onGoBack={() => onGoToPrevious(pageIndex)}
        {...props}
      >
        <OptionsWrapper>
          {map(options, (option, index) => (
            <StyledRadio
              defaultChecked={checkedValue && `${checkedValue}` === `${option}`}
              key={index}
              label={
                hasTranslatedOptions
                  ? translate(`advisor:steps.${pageKey}.options.${option}`)
                  : option
              }
              name={`advisor-${pageKey}`}
              onChange={handleChange}
              value={option}
            />
          ))}

          <OtherWrapper>
            <StyledRadio
              defaultChecked={isOtherOptionChecked}
              key={size(waterCapacityOtherOptions)}
              label={translate('common:labels.other')}
              name={`advisor-${pageKey}`}
              onChange={handleCheckOtherOption}
              value={value}
            />

            <StyledInput
              disabled={!showInput}
              isVisible={showInput}
              name={`advisor-${pageKey}-input`}
              onChange={({ target }) => setValue(target.value)}
              placeholder={translate('forms:placeholders.other')}
              value={isOtherOptionChecked ? value : null}
              {...(pageKey === stepsKeys.waterCapacityOther && {
                unit: modelRangeSpecsMeasures.waterCapacity
              })}
            />
          </OtherWrapper>
        </OptionsWrapper>
      </BaseStep>
    </Wrapper>
  );
};

/**
 * Export `StepOtherOption` component.
 */

export default StepOtherOption;
